import { PlusIcon } from "lucide-react";
import { useState } from "react";
import { SubmitHandler } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { useMutation } from "urql";

import { TournamentStageForm } from "@/components/forms/TournamentStageForm";
import {
    TournamentStageFormInput,
    zTournamentStageFormInput,
} from "@/components/forms/validation/tournament-stage";
import { buttonVariants } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { graphql } from "@/gen";
import { TournamentT } from "@/routes/tournament/queries";
import { cn } from "@/utils";

const createTournamentStageMutation = graphql(`
    mutation createTournamentStage($input: CreateTournamentStageInput!) {
        createTournamentStage(input: $input) {
            id
            ...useTournamentStage_TournamentStage
        }
    }
`);

export const TournamentStageCreateDialog = () => {
    const [_res, createTournamentStage] = useMutation(createTournamentStageMutation);
    const { tournament } = useOutletContext<{ tournament: TournamentT }>();
    const [isOpen, setIsOpen] = useState(false);

    const onSubmit: SubmitHandler<TournamentStageFormInput> = async input => {
        const result = await createTournamentStage({
            input: {
                tournamentId: tournament.id,
                title: input.title,
                description: input.description,
                kind: input.kind,
                order: input.order,
                trialFormat: input.trialFormat,
                tieBreakers: input.tieBreakers,
                eliminationSettings: input.eliminationSettings,
            },
        });

        if (result.error) {
            toast.error("Unable to save tournament stage", { description: result.error.message });
            for (const error of result.error.graphQLErrors) {
                // @ts-expect-error typed as string from the API
                form.setError(error.name, { message: error.message });
            }
        } else {
            toast.success("Tournament stage was created");
            setIsOpen(false);
        }
    };

    const defaultValues = zTournamentStageFormInput.parse({
        title: "",
        description: null,
        kind: "POOL",
        order: 0,
    });

    return (
        <Dialog open={isOpen} onOpenChange={() => setIsOpen(!isOpen)}>
            <DialogTrigger className={cn(buttonVariants())}>
                <PlusIcon />
            </DialogTrigger>
            <DialogContent
                className="max-h-[80vh] w-full max-w-3xl overflow-y-auto"
                aria-describedby={undefined}
            >
                <DialogHeader>
                    <div className="mr-4 flex items-center justify-between">
                        <DialogTitle>Create stage tournament</DialogTitle>
                    </div>
                </DialogHeader>
                <TournamentStageForm onSubmit={onSubmit} defaultValues={defaultValues} />
            </DialogContent>
        </Dialog>
    );
};
